import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useDeleteTeamsBulkDialogContent } from "@components/table/teams/bulk-actions/bulk-delete-teams-helper";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { deleteTeam } from "@store/teams/teams-slice-thunk";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, DeleteIcon } from "@flat-ui";

/** Show delete teams button and dispatch action where the bulk dialog will act accordingly */
export function BulkDeleteTeams({
  companyId,
}: BaseCompanyIdProps): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(selectedEntitiesSelector("teams"));
  const { canDeleteTeams } = useHasUserValidRoleCompanyLevel();

  const dispatch = useAppDispatch();

  const { bulkDialogContent, successDialogContent } =
    useDeleteTeamsBulkDialogContent();
  const mode = useBulkActionsMode();

  // Closes the dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onDeleteTeamClick(): void {
    dispatch(setBulkActionName("removeTeam"));

    for (const entity of selectedEntities) {
      if (!canDeleteTeams) {
        dispatch(
          updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message: `You are not allowed to delete this ${TEAM_DISPLAY_NAME} from workspace`,
            },
          })
        );
      }
    }

    setShouldShowDialog(true);
  }

  const config = getBulkActionButtonConfig(
    mode,
    `Remove ${TEAM_DISPLAY_NAME} from workspace`
  );

  return (
    <>
      <FaroTextButton
        onClick={onDeleteTeamClick}
        tooltipText={config.tooltipText}
        sx={config.sx}
        rippleColor={config.rippleColor}
        startIcon={<DeleteIcon sx={{ width: "18px", height: "18px" }} />}
      >
        {config.shouldShowText && <Text variant="buttonM">Remove</Text>}
      </FaroTextButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "removeTeam" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      {bulkActionName === "removeTeam" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkDeleteTeam}
          bulkActionCallback={(team: FaroCoreApiTypes.Team) =>
            dispatch(
              deleteTeam({
                companyId,
                teamId: team.id,
              })
            ).unwrap()
          }
          selectedEntities={selectedEntities}
          uniqueIdKey="id"
          tableDataType="teams"
        />
      )}
    </>
  );
}
